import { buildApiUrl } from "../config/api";

const TOKEN_KEY = "rgpd_api_token";
const USER_KEY = "rgpd_user";
const TENANT_KEY = "rgpd_tenant";
const ORGANIZATIONS_KEY = "rgpd_organizations";
const TENANT_ROLES_KEY = "rgpd_tenant_roles";
const TRUSTED_DEVICE_KEY = "rgpd_trusted_device";
const READONLY_TOKEN_QUERY = "ro_token";

export const trustedDeviceStorage = {
  get: () => localStorage.getItem(TRUSTED_DEVICE_KEY),
  set: (token) => localStorage.setItem(TRUSTED_DEVICE_KEY, token),
  clear: () => localStorage.removeItem(TRUSTED_DEVICE_KEY),
};

export const tokenStorage = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

/** À appeler une fois au montage de l’app : lit ?ro_token=, stocke le jeton API et retire le paramètre de l’URL. */
export function bootstrapTokenFromUrl() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const ro = params.get(READONLY_TOKEN_QUERY);
  if (!ro?.trim()) return null;
  tokenStorage.set(ro.trim());
  params.delete(READONLY_TOKEN_QUERY);
  const q = params.toString();
  window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : "") + window.location.hash);
  return ro.trim();
}

export const userStorage = {
  get: () => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },
  set: (user) => localStorage.setItem(USER_KEY, JSON.stringify(user || {})),
  clear: () => localStorage.removeItem(USER_KEY),
};

export const tenantStorage = {
  get: () => {
    try {
      const raw = localStorage.getItem(TENANT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },
  set: (tenant) => localStorage.setItem(TENANT_KEY, JSON.stringify(tenant || {})),
  clear: () => localStorage.removeItem(TENANT_KEY),
};

export const organizationsStorage = {
  get: () => {
    try {
      const raw = localStorage.getItem(ORGANIZATIONS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  },
  set: (orgs) => localStorage.setItem(ORGANIZATIONS_KEY, JSON.stringify(Array.isArray(orgs) ? orgs : [])),
  clear: () => localStorage.removeItem(ORGANIZATIONS_KEY),
};

export const tenantRolesStorage = {
  get: () => {
    try {
      const raw = localStorage.getItem(TENANT_ROLES_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  },
  set: (roles) => localStorage.setItem(TENANT_ROLES_KEY, JSON.stringify(Array.isArray(roles) ? roles : [])),
  clear: () => localStorage.removeItem(TENANT_ROLES_KEY),
};

async function request(path, options = {}) {
  const token = tokenStorage.get();
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    let msg = data.message || "Erreur API";
    throw new Error(msg);
  }

  return data;
}

function guessFilenameFromContentType(contentType, fallbackBaseName) {
  if ((contentType || "").includes("application/pdf")) {
    return `${fallbackBaseName}.pdf`;
  }
  if ((contentType || "").includes("application/timestamp-query")) {
    return `${fallbackBaseName}.tsq`;
  }
  if ((contentType || "").includes("application/timestamp-reply")) {
    return `${fallbackBaseName}.tsr`;
  }
  if ((contentType || "").includes("application/x-x509-ca-cert") || (contentType || "").includes("application/pkix-cert")) {
    return `${fallbackBaseName}.crt`;
  }
  if ((contentType || "").includes("text/plain")) {
    return `${fallbackBaseName}.txt`;
  }

  return `${fallbackBaseName}.bin`;
}

function parseFilenameFromContentDisposition(contentDisposition) {
  if (!contentDisposition || typeof contentDisposition !== "string") return null;
  const cd = contentDisposition.trim();
  // RFC 5987 : filename*=UTF-8''percent-encoded
  const star = cd.match(/filename\*=(?:UTF-8''|utf-8'')([^;]+)/i);
  if (star) {
    const raw = star[1].trim().replace(/^"(.*)"$/, "$1");
    try {
      const decoded = decodeURIComponent(raw);
      if (decoded) return decoded;
    } catch {
      /* ignore */
    }
  }
  const mQuoted = cd.match(/filename="((?:[^"\\]|\\.)*)"/i);
  if (mQuoted) return mQuoted[1].replace(/\\(.)/g, "$1");
  const mBare = cd.match(/filename=([^;]+)/i);
  if (mBare) return mBare[1].trim().replace(/^"|"$/g, "");
  return null;
}

async function requestBlob(path, options = {}) {
  const token = tokenStorage.get();
  const headers = {
    ...(options.headers || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || "Erreur API");
  }

  const contentDisposition = response.headers.get("content-disposition") || "";
  const contentType = response.headers.get("content-type") || "";
  const parsedName = parseFilenameFromContentDisposition(contentDisposition);
  const fallbackBaseName = options.fallbackBaseName || "download";
  const preferredRaw = options.preferredFileName;
  const preferred =
    typeof preferredRaw === "string" && preferredRaw.trim() !== "" ? preferredRaw.trim() : null;

  return {
    blob: await response.blob(),
    filename: parsedName || preferred || guessFilenameFromContentType(contentType, fallbackBaseName),
  };
}

export const authApi = {
  login: (email, password, tenantId = null) => {
    const trusted = trustedDeviceStorage.get();
    return request("/login", {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        ...(tenantId != null ? { tenant_id: tenantId } : {}),
        ...(trusted ? { trusted_device_token: trusted } : {}),
      }),
    });
  },
  completeTwoFactorLogin: (challengeToken, code, rememberDevice) =>
    request("/login/two-factor", {
      method: "POST",
      body: JSON.stringify({
        challenge_token: challengeToken,
        code,
        remember_device: !!rememberDevice,
      }),
    }),
  logout: () => request("/logout", { method: "POST" }),
  me: () => request("/me"),
  switchTenant: (tenantId) =>
    request("/switch-tenant", {
      method: "POST",
      body: JSON.stringify({ tenant_id: tenantId }),
    }),
  createOrganization: (payload) =>
    request("/organizations", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  requestOrganizationDeletion: (tenantId) =>
    request(`/organizations/${tenantId}/delete-request`, {
      method: "POST",
    }),
  confirmOrganizationDeletion: (token) =>
    request("/confirm-organization-deletion", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  forgotPassword: (email) =>
    request("/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  resetPassword: (payload) =>
    request("/reset-password", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
};

export const accountApi = {
  updatePassword: (payload) =>
    request("/account/password", {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  updateProfile: (payload) =>
    request("/account/profile", {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  requestEmailChangeCode: (payload) =>
    request("/account/email/request-code", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  confirmEmailChange: (payload) =>
    request("/account/email/confirm", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  mfaSetup: () =>
    request("/account/mfa/setup", {
      method: "POST",
      body: JSON.stringify({}),
    }),
  mfaConfirm: (code) =>
    request("/account/mfa/confirm", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  mfaDisable: (password, code) =>
    request("/account/mfa/disable", {
      method: "POST",
      body: JSON.stringify({ password, code }),
    }),
};

export const tenantCompanyApi = {
  get: () => request("/tenant/company-profile"),
  sync: (companyProfile) =>
    request("/tenant/company-profile", {
      method: "PUT",
      body: JSON.stringify(companyProfile),
    }),
  /** Finalise l’assistant d’onboarding (marque le tenant comme configuré). */
  completeOnboarding: (companyProfilePayload) =>
    request("/tenant/onboarding/complete", {
      method: "POST",
      body: JSON.stringify(companyProfilePayload),
    }),
};

/** Activation des types de document pour le formulaire « Nouvelle preuve » (par tenant). */
export const tenantDocTypesApi = {
  get: () => request("/tenant/doc-types-settings"),
  update: (enabledByCode) =>
    request("/tenant/doc-types-settings", {
      method: "PUT",
      body: JSON.stringify({ enabled: enabledByCode }),
    }),
};

export const teamApi = {
  dashboard: () => request("/team"),
  invite: (payload) =>
    request("/team/invitations", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  resend: (id) =>
    request(`/team/invitations/${id}/resend`, {
      method: "POST",
    }),
  revoke: (id) =>
    request(`/team/invitations/${id}`, {
      method: "DELETE",
    }),
};

export const personsApi = {
  list: () => request("/persons"),
  create: (payload) =>
    request("/persons", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  update: (id, payload) =>
    request(`/persons/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  remove: (id) =>
    request(`/persons/${id}`, {
      method: "DELETE",
    }),
};

export const processorsApi = {
  list: () => request("/processors"),
  sync: (items) =>
    request("/processors/sync", {
      method: "PUT",
      body: JSON.stringify({ items }),
    }),
};

export const processingActivitiesApi = {
  list: () => request("/processing-activities"),
  sync: (items) =>
    request("/processing-activities/sync", {
      method: "PUT",
      body: JSON.stringify({ items }),
    }),
};

/** Suggestion de champs ROPA à partir d’une URL — LLM côté serveur (Claude / Gemini / Ollama selon ROPA_AI_PROVIDER) */
export const ropaAiApi = {
  fillFromUrl: (url) =>
    request("/ropa/ai-fill-from-url", {
      method: "POST",
      body: JSON.stringify({ url }),
    }),
};

/** Bibliothèque de traitements pré configurés (prestataires RGPD) — recherche + import ROPA */
export const hapivotBibliothequeApi = {
  catalog: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
      )
    );
    return request(`/hapivot/bibliotheque?${q.toString()}`);
  },
  import: (ids) =>
    request("/hapivot/bibliotheque/import", {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),
};

export const consentApi = {
  getData: () => request("/consent-data"),
  syncData: (pointsCollecte, retraits) =>
    request("/consent-data/sync", {
      method: "PUT",
      body: JSON.stringify({ pointsCollecte, retraits }),
    }),
};

export const incidentsApi = {
  list: () => request("/incidents"),
  sync: (items) =>
    request("/incidents/sync", {
      method: "PUT",
      body: JSON.stringify({ items }),
    }),
};

export const requestsApi = {
  list: () => request("/data-subject-requests"),
  sync: (items) =>
    request("/data-subject-requests/sync", {
      method: "PUT",
      body: JSON.stringify({ items }),
    }),
};

export const evidencesApi = {
  list: () => request("/evidences"),
  sync: (items) =>
    request("/evidences/sync", {
      method: "PUT",
      body: JSON.stringify({ items }),
    }),
};

export const documentCategoriesApi = {
  list: () => request("/document-categories"),
  sync: (items) => {
    const list = Array.isArray(items) ? items : [];
    return request("/document-categories/sync", {
      method: "PUT",
      body: JSON.stringify({ items: list }),
    });
  },
};

export const activitiesApi = {
  list: () => request("/activities"),
  sync: (items) =>
    request("/activities/sync", {
      method: "PUT",
      body: JSON.stringify({ items }),
    }),
};

export const rightsWorkflowApi = {
  get: () => request("/rights-workflow"),
  sync: (item) =>
    request("/rights-workflow/sync", {
      method: "PUT",
      body: JSON.stringify({ item }),
    }),
};

export const pssiApi = {
  get: () => request("/pssi"),
  sync: (item) =>
    request("/pssi/sync", {
      method: "PUT",
      body: JSON.stringify({ item }),
    }),
};

export const itAssetsApi = {
  list: () => request("/it-assets"),
  sync: (items) =>
    request("/it-assets/sync", {
      method: "PUT",
      body: JSON.stringify({ items }),
    }),
};

export const praApi = {
  get: () => request("/pra-data"),
  sync: (item) =>
    request("/pra-data/sync", {
      method: "PUT",
      body: JSON.stringify({ item }),
    }),
};

export const raciApi = {
  get: () => request("/activity-raci"),
  sync: (matrix) =>
    request("/activity-raci/sync", {
      method: "PUT",
      body: JSON.stringify({ matrix }),
    }),
};

export const reportSchedulesApi = {
  list: () => request("/report-schedules"),
  update: (type, body) =>
    request(`/report-schedules/${encodeURIComponent(type)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
};

export const reportsApi = {
  list: ({ archived = false } = {}) => {
    const q = archived ? "?archived=1" : "";
    return request(`/reports${q}`);
  },
  generate: (type, payload = null) =>
    request(`/reports/${type}/generate`, {
      method: "POST",
      ...(payload != null && Object.keys(payload).length > 0
        ? { body: JSON.stringify(payload) }
        : {}),
    }),
  get: (id) => request(`/reports/${id}`),
  proof: (id) => request(`/reports/${id}/proof`),
  signatures: (id) => request(`/reports/${id}/signatures`),
  sign: (id, { signatureImageBase64 } = {}) =>
    request(`/reports/${id}/sign`, {
      method: "POST",
      body: JSON.stringify(
        signatureImageBase64 ? { signature_image: signatureImageBase64 } : {}
      ),
    }),
  signatureImageBlob: async (reportId, signatureId) => {
    const { blob } = await requestBlob(
      `/reports/${reportId}/signatures/${signatureId}/image`,
      { fallbackBaseName: "signature" }
    );
    return blob;
  },
  remind: (id) =>
    request(`/reports/${id}/remind-signers`, {
      method: "POST",
    }),
  archive: (id) =>
    request(`/reports/${id}/archive`, {
      method: "POST",
    }),
  downloadPdf: (id, opts = {}) =>
    requestBlob(`/reports/${id}/download`, {
      fallbackBaseName: opts.fallbackBaseName ?? `rapport-${id}`,
      preferredFileName: opts.preferredFileName,
    }),
  downloadArtifact: (id, kind) => requestBlob(`/reports/${id}/artifact/${kind}`, { fallbackBaseName: `rapport-${id}-${kind}` }),
};
